import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * EmptyState - Standardized empty state component
 * 
 * Provides consistent empty states across all pages.
 * Use this for "no data" scenarios.
 * 
 * @example
 * ```tsx
 * {data.length === 0 ? (
 *   <EmptyState
 *     title="No experiments yet"
 *     description="Create your first A/B test to start optimizing conversions."
 *     action={<Button onClick={handleCreate}>Create first experiment</Button>}
 *     secondaryAction={<Button variant="outline" asChild><a href="https://docs.humblytics.com" target="_blank">View docs</a></Button>}
 *   />
 * ) : (
 *   <DataTable data={data} />
 * )}
 * ```
 */
interface EmptyStateProps {
  /** Title text */
  title: string
  /** Description text */
  description?: string
  /** Optional primary action button */
  action?: React.ReactNode
  /**
   * Optional secondary action, rendered beside the primary action (e.g. an
   * outline "Docs" / "Live demo" link). Only shown alongside a primary
   * `action` — pass a `Button variant="outline"` or similarly muted control.
   */
  secondaryAction?: React.ReactNode
  /** Optional icon */
  icon?: React.ReactNode
  /**
   * Optional larger illustration slot rendered above the title in a soft
   * muted circle. Use in place of `icon` when the empty state is the sole
   * focus of the page and warrants more visual weight.
   */
  illustration?: React.ReactNode
  /** Custom className */
  className?: string
}

export function EmptyState({
  title,
  description,
  action,
  secondaryAction,
  icon,
  illustration,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-16 px-4',
        className
      )}
    >
      {illustration ? (
        <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-muted/40 border border-border/30 text-muted-foreground/50">
          {illustration}
        </div>
      ) : (
        icon && (
          <div className="mb-4 flex size-10 items-center justify-center rounded-lg bg-muted/35 border border-border/30 text-muted-foreground/50">
            {icon}
          </div>
        )
      )}
      <h3 className="text-base font-medium text-foreground mb-1.5 tracking-tight">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-muted-foreground text-center max-w-md mb-4 leading-relaxed">
          {description}
        </p>
      )}
      {(action || secondaryAction) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  )
}
